import React, { useEffect } from 'react';
import { InformationTableRow } from '@europrocurement/flexy-components';
import { DossierOpenSearch, NestedDossierProcessesOpenSearch } from '@europrocurement/l2d-domain';
import { Box, lighten, SxProps, useTheme } from '@mui/material';
import { LegalAnnoucement } from '@europrocurement/l2d-modules/modules/Dossiers/types';
import { type DossierArticle, useDossierProcesses } from '@europrocurement/l2d-modules';
import DossierArticleProcessMenu, {
    DossierArticleProcessMenuItemProps,
} from '../DossierArticleProcessMenu/DossierArticleProcessMenu';
import DossierArticleProcessStatus from '../DossierArticleProcessStatus/DossierArticleProcessStatus';
import DossierArticlesProcessIcons from '../DossierArticleProcessIcons/DossierArticleProcessIcons';

export type DossierArticlesProcessesListProps = {
    articles: DossierArticle[];
    dossier: DossierOpenSearch;
    validatorTypeId: number;
    legalAnnoucements: Array<LegalAnnoucement>;
    getItemActions?: (article: DossierArticle) => Array<DossierArticleProcessMenuItemProps>;
};

const getKeyFromIndex = (index: number, prefix: string) => `${prefix}-${index}`;

type DossierArticleWithValidatorProcesses = {
    article: DossierArticle;
    processes: Array<NestedDossierProcessesOpenSearch>;
};

type mergeArticleByValidatorProcessId = {
    [key: string]: DossierArticleWithValidatorProcesses[];
};

const DossierArticlesProcessesTaxList: React.FunctionComponent<DossierArticlesProcessesListProps> =
    function ({
        articles,
        dossier,
        validatorTypeId,
        legalAnnoucements,
        getItemActions = () => [],
    }) {
        const { palette } = useTheme();
        const { getProcessesByArticle } = useDossierProcesses();
        const [mergedArticlesByValidatorProcess, setMergedArticlesByValidatorProcess] =
            React.useState<mergeArticleByValidatorProcessId>({});
        const returnMergedArticlesByValidatorProcess = () => {
            const articlesAndProcesses: DossierArticleWithValidatorProcesses[] = articles.map(
                (article) => ({
                    article,
                    processes: getProcessesByArticle(dossier, validatorTypeId, article.id),
                }),
            );

            // Merge articles by validator process id
            // If an article has no process, it will be added to the 'withoutProcess'
            const mergedArticles: mergeArticleByValidatorProcessId = {};
            mergedArticles.withoutProcess = [];

            articlesAndProcesses.forEach((artWithProcess) => {
                const processId = artWithProcess.processes[0]?.id;
                if (processId) {
                    if (!mergedArticles[processId]) {
                        mergedArticles[processId] = [];
                    }
                    mergedArticles[processId].push(artWithProcess);
                } else {
                    mergedArticles.withoutProcess.push(artWithProcess);
                }
            });
            setMergedArticlesByValidatorProcess(mergedArticles);
        };

        useEffect(() => {
            returnMergedArticlesByValidatorProcess();
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [articles]);

        const tableHeadings = ['', 'N°', 'Démarches', 'Informations', 'Statuts', ''];

        const cellsSx: SxProps[] = [
            { width: '0%' },
            { width: '5%' },
            { width: '45%' },
            { width: '30%' },
            { width: '10%' },
            { width: '10%' },
        ];

        const getSxFromIndex = (index: number): SxProps | undefined => {
            if (cellsSx && cellsSx[index]) {
                return cellsSx[index] as SxProps;
            }
            return undefined;
        };

        const headerCels = tableHeadings.map((header, headerIndex) => {
            let sx: SxProps = {
                border: 'none',
            };

            if (cellsSx) {
                const cellPropsSx = getSxFromIndex(headerIndex) as SxProps;
                sx = { ...sx, ...cellPropsSx };
            }
            return (
                <Box
                    sx={{
                        border: 'none',
                        padding: '2px 8px',
                        lineHeight: '1.6',
                        fontWeight: '500',
                        fontSize: '13px',
                        verticalAlign: 'middle',
                        color: lighten(palette.secondary.main, 0.5),
                        minWidth: '20px',
                        display: 'block',
                        ...sx,
                    }}
                    key={getKeyFromIndex(headerIndex, 'header')}
                >
                    {header}
                </Box>
            );
        });

        const renderInformationRow = (
            article: DossierArticle,
            processes: NestedDossierProcessesOpenSearch[],
            index: number,
        ) => {
            const process = processes[0] || undefined;

            let updateValidatorAction: DossierArticleProcessMenuItemProps | undefined =
                {} as DossierArticleProcessMenuItemProps;
            if (getItemActions) {
                const itemsActions = getItemActions(article);
                if (itemsActions) {
                    updateValidatorAction = itemsActions.find(
                        (action) => action.label === 'Modifier le statut valideur',
                    );
                }
            }

            return (
                <InformationTableRow
                    key={getKeyFromIndex(index + 1, 'row')}
                    cels={[
                        null,
                        index + 1,
                        article.label,
                        <DossierArticlesProcessIcons
                            article={article}
                            dossier={dossier}
                            validatorTypeId={validatorTypeId}
                            legalAnnoucements={legalAnnoucements}
                        />,
                        process !== undefined ? (
                            <DossierArticleProcessStatus
                                statusFlag={process.statusFlag}
                                statusLabel={process.statusLabel}
                                onClick={() => updateValidatorAction?.onClick(article)}
                            />
                        ) : null,
                        <DossierArticleProcessMenu
                            article={article}
                            getItemActions={getItemActions}
                        />,
                    ]}
                    cellsSx={cellsSx}
                />
            );
        };

        return (
            <Box
                sx={{
                    borderCollapse: 'separate',
                    borderSpacing: '0 4px',
                }}
            >
                <Box>
                    <Box
                        sx={{ width: '100%', display: 'flex' }}
                        data-testid="header-tax-list"
                    >
                        {headerCels}
                    </Box>
                </Box>
                <Box>
                    {Array.from(Object.entries(mergedArticlesByValidatorProcess)).map(
                        ([key, values], index) => {
                            if (key === 'withoutProcess') {
                                return values.map((artValidator) =>
                                    renderInformationRow(
                                        artValidator.article,
                                        artValidator.processes,
                                        index,
                                    ),
                                );
                            }
                            return (
                                <Box
                                    sx={{
                                        width: '100%',
                                        borderRadius: '12px',
                                        display: 'block',
                                    }}
                                    key={getKeyFromIndex(index, 'box')}
                                >
                                    {values.map((artValidator) =>
                                        renderInformationRow(
                                            artValidator.article,
                                            artValidator.processes,
                                            index,
                                        ),
                                    )}
                                </Box>
                            );
                        },
                    )}
                </Box>
            </Box>
        );
    };

export default DossierArticlesProcessesTaxList;
